import { message, Radio, Switch, UploadFile } from "antd";
import type { RcFile } from "antd/es/upload";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import AppHeader from "../../../../components/AppHeader/AppHeader";
import AddOrCreateScriptCategory from "../../../../components/chatScripts/AddOrCreateScriptCategory/AddOrCreateScriptCategory";
import ChatScriptFilesUpload from "../../../../components/chatScripts/ChatScriptFilesUpload/ChatScriptFilesUpload";
import Loader from "../../../../components/loader/Loader";
import MessageScriptTextArea from "../../../../components/MessageScriptTextArea/MessageScriptTextArea";
import PriceInputComponent from "../../../../components/PriceInputComponent/PriceInputComponent";
import SpinnerComponent from "../../../../components/SpinnerComponent/SpinnerComponent";
import StickyButton from "../../../../components/StickyButtonComponent/StickyButtonComponent";
import { getDiscountExpirePeriods, getMediaAmounts, MAX_MEDIAS_TO_SEND, MAX_PRICE } from "../../../../helpers/constant";
import { FilePrivacy } from "../../../../helpers/types";
import useFetchUserInfos from "../../../../hooks/userHooks/useFetchUserInfos";
import { clearScriptsCache } from "../../../../redux/chatScript/slices/scriptSlice";
import { fetchScriptsByFilter } from "../../../../redux/chatScript/thunks/scriptThunks";
import { useAppDispatch } from "../../../../redux/hooks";
import { chatScriptService } from "../../../../services/chat-script.service";
import { storageService } from "../../../../services/storage.service";
import { socket } from "../../../../socket";
import { generateThumbnailURL, generateURL } from "../../../../utls/FunctionsUtil";
import AudioIcon from "../../../../assets/icons/white-audio.svg";
import "../ChatScriptsCreateView/ChatScriptsCreateView.less";

const ChatScriptUpdateView: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { t } = useTranslation();

  const [step, setStep] = useState<1 | 2>(1);
  const [title, setTitle] = useState("");
  const [files, setFiles] = useState<any[]>([]);
  type PrivateType = { showDialog: boolean; fileId: string; fileSrc: any; fileType: string };
  const [privateFiles, setPrivateFiles] = useState<PrivateType[]>([]);
  const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);
  const [messageText, setMessageText] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const mediaAmounts = getMediaAmounts(t);
  const discountExpirePeriods = getDiscountExpirePeriods(t);
  const firstThreePeriods = discountExpirePeriods.slice(0, 3);
  const lastThreePeriods = discountExpirePeriods.slice(3);
  const [radioValue, setRadioValue] = useState<string | undefined>("");
  const [customPrice, setCustomPrice] = useState<number | undefined>(undefined);
  const [promoCodeChecked, setPromoCodeChecked] = useState(true);
  const [discountCurrency, setDiscountCurrency] = useState<string | undefined>("");
  const [discountPercentage, setDiscountPercentage] = useState<string | undefined>(undefined);
  const [selectedDiscountPeriod, setSelectedDiscountPeriod] = useState<number | undefined>();
  const [loading, setLoading] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [selectedCategoryName, setSelectedCategoryName] = useState<string>("");
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const { currentUser } = useFetchUserInfos();
  const currentCreator = currentUser?.id;

  const [progress, setProgress] = useState<number>(0);
  const [loadedData, setLoadedData] = useState<number>(0);
  const [totalSize, setTotalSize] = useState<number>(0);

  const [deletedFiles, setDeletedFiles] = useState<string[]>([]);
  const [original, setOriginal] = useState<{
    title?: string;
    message?: string;
    notes?: string;
    price?: number | null;
    promo?: boolean;
    discount?: number;
    discountType?: "CURRENCY" | "PERCENTAGE" | string;
    expireTime?: number;
    expireTimeType?: "HOURS" | "DAYS" | "UNLIMITED" | string;
    categoryId?: string;
    fileIds?: string[];
  }>({});
  const promoContentRef = useRef<HTMLDivElement>(null);
  const isContinueDisabled = useMemo(() => title.trim().length === 0, [title]);
  const noFiles = useMemo(() => !(Array.isArray(files) && files.length > 0), [files]);
  const isConfirmDisabled = useMemo(() => {
    const hasMessage = !!messageText && messageText.trim() !== "";
    const hasFiles = Array.isArray(files) && files.length > 0;
    const hasExplicitPrice = customPrice !== undefined || radioValue !== undefined;

    const promoNeedsValue = promoCodeChecked
      ? (!!discountCurrency && Number(discountCurrency) > 0) || (!!discountPercentage && Number(discountPercentage) > 0)
      : true;
    const promoNeedsDate = promoCodeChecked ? selectedDiscountPeriod !== undefined : true;

    if (!hasMessage) return true;
    if (hasFiles && !hasExplicitPrice) return true;
    if (!promoNeedsValue || !promoNeedsDate) return true;
    return false;
  }, [messageText, files, customPrice, radioValue, promoCodeChecked, discountCurrency, discountPercentage, selectedDiscountPeriod]);

  useEffect(() => {
    if (totalSize > 0) {
      const uploadProgress = Math.round((loadedData / totalSize) * 100);
      const globalProgress = Math.round(uploadProgress / 2);
      setProgress((prev) => {
        if (globalProgress > prev) {
          return globalProgress;
        }
        return prev;
      });
    }
  }, [loadedData]);

  useEffect(() => {
    function onProgressUpdate(data: number) {
      setProgress((prev) => {
        const prog = prev + 1;
        if (prog <= 99) {
          return prog;
        }
        return prev;
      });
    }

    function onProcessEnd(data: any) {
      setLoading(false);
      message.success(t("script-updated-successfully"));
      //navigate("/");
    }
    socket.on("file-updated", onProcessEnd);
    socket.on("progress-update", onProgressUpdate);
    return () => {
      socket.off("file-updated", onProcessEnd);
      socket.off("progress-update", onProgressUpdate);
    };
  }, []);

  // Force price to be free when there are no files
  useEffect(() => {
    if (files.length === 0) {
      const freeOption = mediaAmounts.find((opt) => String(opt.value) === "0" || parseInt(String(opt.value), 10) === 0);
      setCustomPrice(undefined);
      setRadioValue(freeOption ? String(freeOption.value) : "0");
      setPromoCodeChecked(false);
    }
  }, [files.length, mediaAmounts]);

  const handleContinue = () => setStep(2);

  const handleConfirm = async () => {
    const params = new URLSearchParams(location.search);
    const id = params.get("id");
    if (!id) {
      message.error(t("back-error-message"));
      return;
    }
    try {
      setLoading(true);

      const payload = new FormData();
      if (currentCreator) payload.append("creatorId", currentCreator);

      // Generate unique filenames for new files
      const fileNameMap = new Map();
      (files || []).forEach((file: any) => {
        const isNewLocal = file?.originFileObj && file.imgType !== "quick-file";
        if (isNewLocal) {
          const uniqueFileName = generateUniqueFileName(file.name);
          fileNameMap.set(file.uid, uniqueFileName);
        }
      });

      const trimmedTitle = title.trim();
      if (trimmedTitle !== (original.title || "")) payload.append("title", trimmedTitle);

      const trimmedMsg = (messageText || "").trim();
      if (trimmedMsg !== (original.message || "")) payload.append("message", trimmedMsg);

      const trimmedNotes = (note || "").trim();
      if (trimmedNotes !== (original.notes || "")) payload.append("notes", trimmedNotes);

      if ((selectedCategoryId || "") !== (original.categoryId || "")) payload.append("categoryId", selectedCategoryId || "");

      // If there are no files, force price to be 0
      const finalPrice = files.length === 0 ? 0 : customPrice ?? (radioValue != null && radioValue !== "" ? parseInt(radioValue, 10) : 0);
      if ((original.price ?? 0) !== finalPrice) payload.append("price", String(finalPrice));

      const origPromo = !!original.promo;
      // If there are no files, force promo to be false
      const finalPromo = files.length === 0 ? false : promoCodeChecked;
      if (origPromo !== !!finalPromo) payload.append("promo", String(finalPromo));

      const discountValue = discountCurrency ?? discountPercentage;
      const discountType = discountCurrency ? "CURRENCY" : discountPercentage ? "PERCENTAGE" : undefined;
      if (promoCodeChecked) {
        if (typeof original.discount !== "number" || Number(discountValue ?? 0) !== Number(original.discount ?? 0)) {
          if (discountValue) payload.append("discount", String(Number(discountValue)));
        }
        if ((discountType || "") !== (original.discountType || "")) {
          if (discountType) payload.append("discountType", discountType);
        }
        const rawExpire = selectedDiscountPeriod !== undefined ? discountExpirePeriods[selectedDiscountPeriod] : undefined;
        const expireTime = rawExpire ? Number(rawExpire.value.value) : undefined;
        const expireTimeType = rawExpire ? (rawExpire.value.timeType as "HOURS" | "DAYS" | "UNLIMITED") : undefined;
        if (typeof expireTime !== "undefined" && expireTime !== (original.expireTime as any)) payload.append("expireTime", String(expireTime));
        if ((expireTimeType || "") !== (original.expireTimeType || "")) {
          if (expireTimeType) payload.append("expireTimeType", expireTimeType);
        }
      }

      // Create filesPrivacy array with originalFileName for new files
      const filesPrivacy: Array<FilePrivacy & { originalFileName?: string }> = [];
      (files || []).forEach((file: any) => {
        const isNewLocal = file?.originFileObj && file.imgType !== "quick-file";
        const filePrivacy: FilePrivacy & { originalFileName?: string } = {
          fileId: file.uid,
          isPrivate: !!file.isPrivate,
        };

        if (isNewLocal) {
          filePrivacy.originalFileName = fileNameMap.get(file.uid) || file.name;
        }

        filesPrivacy.push(filePrivacy);
      });

      payload.append("fileIds", JSON.stringify(filesPrivacy));

      const newQuickFiles = (files || [])
        .filter((f: any) => f.imgType === "quick-file" && !(original.fileIds || []).includes(f.uid))
        .map((f: any) => ({ id: f.uid, isPrivate: !!f.isPrivate }));

      if (newQuickFiles.length > 0) {
        payload.append("quickFiles", JSON.stringify(newQuickFiles));
      }

      if (deletedFiles.length > 0) payload.append("deletedFiles", JSON.stringify(deletedFiles));

      const videoFileToChunk: Array<{ file: any; isPrivate: boolean; name: string; size: number }> = [];
      (files || []).forEach((file: any) => {
        const isNewLocal = file?.originFileObj && file.imgType !== "quick-file";
        if (!isNewLocal) return;
        const raw = file.originFileObj as RcFile | undefined;
        if (!raw) return;

        const uniqueFileName = fileNameMap.get(file.uid);

        if (raw.type?.includes("image/")) {
          const newFile = new File([raw], uniqueFileName, { type: raw.type });
          payload.append("files", newFile);
        } else if (raw.type?.includes("audio/")) {
          const newFile = new File([raw], uniqueFileName, { type: raw.type });
          payload.append("files", newFile);
        } else if (raw.type?.includes("video/")) {
          videoFileToChunk.push({ file, isPrivate: !!file.isPrivate, name: file.name, size: file.size });
        }
      });

      if (videoFileToChunk.length > 0) {
        const totalSizes = videoFileToChunk.reduce((acc: number, f) => acc + f.size, 0);
        setTotalSize(totalSizes);
        const videoFiles: any[] = [];
        for (let i = 0; i < videoFileToChunk.length; i++) {
          const vf = videoFileToChunk[i];
          const result = await storageService.uploadFileMultiPart(vf.file, setLoadedData, totalSize);
          videoFiles.push({
            Location: result.Location,
            isPrivate: vf.isPrivate,
            Key: result.Key,
            ETag: result.ETag,
            Bucket: result.Bucket,
            filename: vf.name,
            size: vf.size,
          });
        }
        payload.append("videoFiles", JSON.stringify(videoFiles));
      }

      await chatScriptService.updateScriptV2(id, payload, setProgress).then((res: any) => {
        if (res) {
          message.success(t("script-updated-successfully"));
        }
      });

      if (currentCreator) {
        try {
          dispatch(clearScriptsCache());
          await (dispatch as any)(fetchScriptsByFilter({ creatorId: currentCreator, filterOrCategoryId: "all", page: 1 }));
        } catch {}
      }

      // Check if there's a 'from' parameter to navigate back to
      const params = new URLSearchParams(location.search);
      const fromParam = params.get("from");

      if (fromParam === "settings") {
        // If coming from settings, navigate back to the settings page
        navigate("/common/setting");
      } else {
        // Default navigation
        navigate("/creator/chat-scripts");
      }
    } catch (e) {
      console.error("update script failed", e);
      message.error(t("back-error-message"));
    } finally {
      setLoading(false);
    }
  };

  const handleCustomPriceChange = (value: number | null) => {
    if (noFiles) return;
    if (value !== null) {
      setCustomPrice(value);
      setRadioValue(undefined);
      if (value > MAX_PRICE) message.warning(t("maximum-price-alert"));
    }
  };

  const handleRadioChange = (e: any) => {
    if (noFiles) return;
    const value = e.target.value;
    setRadioValue(value);
    setCustomPrice(parseInt(value));
    if (value > MAX_PRICE) message.warning(t("maximum-price-alert"));
  };

  const handlePromoToggle = (checked: boolean) => {
    setPromoCodeChecked(checked);
    if (checked) {
      setTimeout(() => {
        promoContentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;
    if (e.ctrlKey || e.metaKey || key === "Backspace" || key === "Tab" || key === "ArrowLeft" || key === "ArrowRight" || key === "Delete") return;
    if (key < "0" || key > "9") e.preventDefault();
  };

  const handleDiscountCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDiscountCurrency(value);
    setDiscountPercentage(undefined);
  };

  const handleDiscountPercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = parseInt(e.target.value);
    if (!isNaN(value)) {
      if (value > 100) value = 100;
      else if (value < 0) value = 0;
    } else {
      value = 0;
    }
    setDiscountPercentage(value.toString());
    setDiscountCurrency(undefined);
  };

  const handleExpireTimeChange = (_period: { value: string; timeType: string }, index: number) => {
    setSelectedDiscountPeriod(index);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const keep = window.localStorage.getItem("chatScriptsUpdate.keep") === "1";
    const selected = params.get("selected");

    if (keep || selected) {
      const storedTitle = window.localStorage.getItem("chatScriptsUpdate.title");
      if (storedTitle) setTitle(storedTitle);
      if (step !== 2) setStep(2);
      window.localStorage.removeItem("chatScriptsUpdate.keep");
    }

    const name = params.get("name");
    if (name && !keep && !selected) setTitle(name);

    const id = params.get("id");
    if (id) {
      let mounted = true;
      (async () => {
        try {
          setDetailLoading(true);
          const data = await chatScriptService.getScriptById(id);
          if (!mounted) return;
          if (data?.title && !keep && !selected) setTitle(data.title);
          if (typeof data?.message === "string") setMessageText(data.message);
          if (typeof data?.notes === "string") setNote(data.notes);
          const price = (data?.price as number | null | undefined) ?? null;
          if (price && price > 0) {
            setCustomPrice(price);
            setRadioValue(undefined);
          } else {
            setCustomPrice(undefined);
            const freeOption = mediaAmounts.find((opt) => String(opt.value) === "0" || parseInt(String(opt.value), 10) === 0);
            setRadioValue(freeOption ? String(freeOption.value) : "0");
          }
          const promo = (data as any)?.promo;
          setPromoCodeChecked(!!promo && !!price && price > 0);
          const discountType = (data as any)?.discountType as string | undefined;
          const discount = (data as any)?.discount as number | undefined;
          if (discountType === "PERCENTAGE") {
            setDiscountPercentage(typeof discount === "number" ? String(Math.min(Math.max(discount, 0), 100)) : "");
            setDiscountCurrency(undefined);
          } else {
            setDiscountCurrency(typeof discount === "number" ? String(Math.max(discount, 0)) : "");
            setDiscountPercentage(undefined);
          }
          const expireTimeType = (data as any)?.expireTimeType as string | undefined;
          const expireTime = (data as any)?.expireTime as number | undefined;
          if (expireTimeType) {
            const idx = discountExpirePeriods.findIndex((p) => p.value.timeType === expireTimeType && p.value.value === String(expireTime ?? 0));
            if (idx >= 0) setSelectedDiscountPeriod(idx);
            else setSelectedDiscountPeriod(undefined);
          } else {
            setSelectedDiscountPeriod(undefined);
          }
          const catObj = (data as any)?.category || (data as any)?.categoryId;
          const catId = typeof catObj === "string" ? catObj : catObj?._id || "";
          if (catId) setSelectedCategoryId(catId);
          const catName = typeof catObj === "object" ? catObj?.name || "" : "";
          if (catName) setSelectedCategoryName(catName);
          const dataFiles = Array.isArray((data as any)?.files) ? (data as any).files : [];
          const mapped = dataFiles.map((f: any) => {
            const isAudio = (f.mimeType || "").toLowerCase().startsWith("audio");
            return {
              uid: f._id,
              name: f.name,
              status: "done" as const,
              url: f.url,
              thumbUrl: isAudio ? AudioIcon : f.thumbnailUrl,
              type: f.mimeType,
              isPrivate: !!f.isPrivate,
              imgType: "quick-file",
            };
          });

          if (selected && !keep) {
            try {
              const selectedIds = JSON.parse(selected);
              if (Array.isArray(selectedIds) && selectedIds.length > 0) {
                const { fileService } = await import("../../../../services/file.service");
                const selectedFiles = await fileService.allSelectedFiles(selectedIds);

                const selectedMapped = selectedFiles.map((item: any) => {
                  let fileType = "";
                  let fileExt = "";
                  let isVideo = false;
                  let isAudio = false;
                  if (item.url.includes(".mp4")) {
                    fileType = "video/mp4";
                    fileExt = "mp4";
                    isVideo = true;
                  } else if (
                    item.url.includes(".mp3") ||
                    item.url.includes(".wav") ||
                    item.url.includes(".m4a") ||
                    item.url.includes(".ogg") ||
                    item.url.includes(".aac") ||
                    item.url.includes(".webm")
                  ) {
                    // Determine specific audio type based on extension
                    if (item.url.includes(".mp3")) {
                      fileType = "audio/mp3";
                      fileExt = "mp3";
                    } else if (item.url.includes(".wav")) {
                      fileType = "audio/wav";
                      fileExt = "wav";
                    } else if (item.url.includes(".m4a")) {
                      fileType = "audio/m4a";
                      fileExt = "m4a";
                    } else if (item.url.includes(".ogg")) {
                      fileType = "audio/ogg";
                      fileExt = "ogg";
                    } else if (item.url.includes(".aac")) {
                      fileType = "audio/aac";
                      fileExt = "aac";
                    } else if (item.url.includes(".webm")) {
                      fileType = "audio/webm";
                      fileExt = "webm";
                    }
                    isAudio = true;
                  } else {
                    fileType = "image/webp";
                    fileExt = "webp";
                  }

                  const originFileObj = {
                    isPrivate: true,
                    status: "done",
                    uid: item._id,
                    name: item._id,
                    percent: 0,
                    thumbUrl: "",
                    type: "quick-link",
                  };
                  const JsonString = JSON.stringify(originFileObj);
                  const blob = new Blob([JsonString], { type: fileType });
                  const file = new File([blob], `newQuickFile.${fileExt}`, { type: fileType });

                  return {
                    ...item,
                    imgType: "quick-file",
                    isPrivate: true,
                    status: "done" as const,
                    uid: item._id,
                    name: item._id,
                    percent: 0,
                    thumbUrl: isVideo ? item?.thumbnailUrl : isAudio ? AudioIcon : item?.url,
                    type: fileType,
                    originFileObj: file,
                  };
                });

                const existingIds = new Set(mapped.map((f: any) => f.uid));
                const newFiles = selectedMapped.filter((f: any) => !existingIds.has(f.uid));
                setFiles([...mapped, ...newFiles]);
              } else {
                setFiles(mapped);
              }
            } catch (e) {
              console.error("Error parsing selected files:", e);
              setFiles(mapped);
            }
          } else {
            setFiles(mapped);
          }

          setOriginal({
            title: data?.title,
            message: (data as any)?.message,
            notes: (data as any)?.notes,
            price: (data as any)?.price ?? null,
            promo: (data as any)?.promo,
            discount: (data as any)?.discount,
            discountType: (data as any)?.discountType,
            expireTime: (data as any)?.expireTime,
            expireTimeType: (data as any)?.expireTimeType,
            categoryId: catId,
            fileIds: dataFiles.map((f: any) => f._id).filter(Boolean),
          });
        } catch (e) {
          console.error("Failed to fetch script details for update", e);
        } finally {
          if (mounted) setDetailLoading(false);
        }
      })();
      return () => {
        mounted = false;
      };
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem("chatScriptsUpdate.title", title);
  }, [title]);

  useEffect(() => {
    return () => {
      const keep = window.localStorage.getItem("chatScriptsUpdate.keep") === "1";
      if (!keep) window.localStorage.removeItem("chatScriptsUpdate.title");
    };
  }, []);

  function generateUniqueFileName(originalName: string): string {
    const lastDotIndex = originalName.lastIndexOf(".");
    const extension = lastDotIndex !== -1 ? originalName.substring(lastDotIndex) : "";
    const nameWithoutExtension = lastDotIndex !== -1 ? originalName.substring(0, lastDotIndex) : originalName;

    const timestamp = Date.now();
    const randomNum = Math.floor(Math.random() * 10000);
    const uniqueId = `${timestamp}_${randomNum}`;

    return `${nameWithoutExtension}_${uniqueId}${extension}`;
  }

  const onFilesChange = async (newList: UploadFile<[]>[] = []) => {
    const prevList = files || [];
    const existingIds = new Set(prevList.map((f: any) => f.uid));
    const newIds = new Set((newList || []).map((f: any) => f.uid));

    let hasChanged = prevList.length !== newList.length;

    if (!hasChanged) {
      const existingIdsArray = Array.from(existingIds);
      const newIdsArray = Array.from(newIds);

      for (const id of existingIdsArray) {
        if (!newIds.has(id)) {
          hasChanged = true;
          break;
        }
      }

      if (!hasChanged) {
        for (const id of newIdsArray) {
          if (!existingIds.has(id)) {
            hasChanged = true;
            break;
          }
        }
      }
    }

    const added = (newList || []).filter((f: any) => !existingIds.has(f.uid) && f.status === "done");

    if (added.length > 0) {
      const filesTSP: PrivateType[] = await Promise.all(
        added.map(async (file: any) => {
          let src = file.url as string;
          if (file.imgType !== "quick-file") {
            if (file.type?.includes("video")) {
              src = await generateThumbnailURL(file.originFileObj as RcFile);
            } else {
              src = await generateURL(file.originFileObj as RcFile);
            }
          } else {
            src = file.thumbUrl!;
          }
          return { showDialog: true, fileId: file.uid, fileSrc: src, fileType: file.type! };
        })
      );
      const seen = new Set<string>();
      const uniq = [...privateFiles, ...filesTSP].filter((f) => {
        if (seen.has(f.fileId)) return false;
        seen.add(f.fileId);
        return true;
      });
      setPrivateFiles(uniq);

      if (privateFiles.length === 0) {
        setCurrentFileIndex(0);
      }

      const addedIds = new Set(added.map((f: any) => f.uid));
      (newList || []).forEach((f: any) => {
        if (addedIds.has(f.uid)) f.isPrivate = true;
      });
    }

    const nextIds = new Set((newList || []).map((f: any) => f.uid));
    const removedIds: string[] = [];
    prevList.forEach((f: any) => {
      if (!nextIds.has(f.uid)) removedIds.push(f.uid);
    });
    if (removedIds.length > 0) {
      const originalSet = new Set(original.fileIds || []);
      const toDelete = removedIds.filter((id) => originalSet.has(id));
      if (toDelete.length > 0) {
        setDeletedFiles((prev) => Array.from(new Set([...prev, ...toDelete])));
      }
    }

    // Reset price only if the file list has actually changed
    if (hasChanged) {
      const freeOption = mediaAmounts.find((opt) => String(opt.value) === "0" || parseInt(String(opt.value), 10) === 0);
      setCustomPrice(undefined);
      setRadioValue(freeOption ? String(freeOption.value) : "0");
      setPromoCodeChecked(false);
    }

    setFiles(newList || []);
  };

  // Reset price and promo when files are cleared
  useEffect(() => {
    if (!files || files.length === 0) {
      if (customPrice !== undefined) setCustomPrice(undefined);
      if (radioValue !== undefined) setRadioValue(undefined);
      if (promoCodeChecked) setPromoCodeChecked(false);
      if (discountCurrency !== undefined) setDiscountCurrency(undefined);
      if (discountPercentage !== undefined) setDiscountPercentage(undefined);
      if (selectedDiscountPeriod !== undefined) setSelectedDiscountPeriod(undefined);
    }
  }, [files]);

  const onNextClick = (privateFile: PrivateType) => {
    setPrivateFiles((prev) => prev.filter((f) => f.fileId !== privateFile.fileId));
    setCurrentFileIndex((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const handleLockSwitchChange = (checked: boolean, privateFile: PrivateType) => {
    setFiles((prev: any[]) =>
      (prev || []).map((file: any) => {
        if (file.uid === privateFile.fileId) {
          return { ...file, isPrivate: checked };
        }
        return { ...file, isPrivate: file.isPrivate !== undefined ? file.isPrivate : true };
      })
    );
  };

  const handleRemoveFile = (fileId: string) => {
    setPrivateFiles((prev) => prev.filter((f) => f.fileId !== fileId));
    const updatedFiles = (files || []).filter((f: any) => f.uid !== fileId);
    setFiles(updatedFiles);
    const wasOriginal = (original.fileIds || []).includes(fileId);
    if (wasOriginal) setDeletedFiles((prev) => (prev.includes(fileId) ? prev : [...prev, fileId]));
  };

  return (
    <div className="chat-scripts-create-view">
      {privateFiles.length === 0 && (
        <AppHeader
          title={t("edit-a-script")}
          backButtonDestination={(() => {
            const params = new URLSearchParams(location.search);
            const fromParam = params.get("from");

            return fromParam === "settings" ? "/common/setting" : -1;
          })()}
          showBackButton={true}
          onBackClick={step === 2 ? () => setStep(1) : undefined}
        />
      )}

      <div className="chat-scripts-create-content" style={{ minHeight: privateFiles.length > 0 ? "100vh" : "auto" }}>
        {step === 1 ? (
          detailLoading ? (
            <div className="step step-title create-form">
              <div className="centered-block">
                <div className="form-container" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200 }}>
                  <Loader />
                </div>
              </div>
            </div>
          ) : (
            <div className="step step-title create-form">
              <div className="centered-block">
                <div className="form-container">
                  <h2 className="chat-script-create-form-title">{t("give-your-script-a-name")}</h2>
                  <div className="chat-script-create-input-group">
                    <input
                      type="text"
                      className="chat-script-create-title-input"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder={t("my-name") as string}
                      autoFocus
                    />
                    <div className="chat-script-create-input-underline" />
                  </div>
                </div>
              </div>
              <div className="bottom-button-wrapper">
                <button
                  className="chat-script-create-button chat-script-create-button--default"
                  onClick={handleContinue}
                  disabled={isContinueDisabled}
                >
                  {t("continue")}
                </button>
              </div>
            </div>
          )
        ) : (
          <div className="chat-scripts-step-2" style={{ display: privateFiles.length > 0 ? "none" : "" }}>
            <div className="chat-scripts-content">
              {/* Media upload section */}
              <div className="media-section">
                <ChatScriptFilesUpload
                  maxCount={MAX_MEDIAS_TO_SEND}
                  onFilesChange={onFilesChange}
                  defaultList={files}
                  context="update"
                  scriptId={new URLSearchParams(location.search).get("id") || ""}
                />
                <p className="section-description">{t("send-file-description")}</p>
              </div>

              {/* Add or create script category selector */}
              <div className="script-category-section">
                {currentCreator && (
                  <AddOrCreateScriptCategory
                    creatorId={currentCreator}
                    selectedCategoryId={selectedCategoryId}
                    selectedCategoryName={selectedCategoryName}
                    onSelect={setSelectedCategoryId}
                  />
                )}
              </div>

              {/* Message text area */}
              <div className="message-section">
                <MessageScriptTextArea
                  title={t("message")}
                  value={messageText}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessageText(e.target.value)}
                  placeholder={`${t("your-message")}...`}
                />
              </div>

              {/* Price selection */}
              {files.length === 0 ? (
                <></>
              ) : (
                <div className="price-section">
                  <PriceInputComponent customPrice={customPrice} handleCustomPriceChange={handleCustomPriceChange} />

                  <Radio.Group className="price-options-group" value={radioValue} onChange={handleRadioChange}>
                    <div className="price-options-container">
                      {mediaAmounts.map((option, index) => (
                        <Radio.Button key={index} value={option.value} className="price-option-button">
                          {option.label}
                        </Radio.Button>
                      ))}
                    </div>
                  </Radio.Group>
                </div>
              )}

              {/* Promotion section - only show when there are files */}
              {files.length > 0 && (
                <div className="promo-section">
                  <div className="promo-header">
                    <h3 className="promo-title">{t("add-a-promotion")}</h3>
                    <label className="promo-switch">
                      <input
                        type="checkbox"
                        checked={promoCodeChecked}
                        onChange={(e) => handlePromoToggle(e.target.checked)}
                        disabled={!customPrice || customPrice <= 0}
                      />
                      <span className="promo-slider"></span>
                    </label>
                  </div>

                  {promoCodeChecked && (
                    <div className="promo-content" ref={promoContentRef}>
                      <div className="discount-section">
                        <h4 className="discount-title">{t("discount")}</h4>
                        <div className="discount-inputs">
                          <div className={`discount-input-container ${discountCurrency ? "active" : ""}`}>
                            <input
                              type="number"
                              placeholder="0"
                              value={discountCurrency || ""}
                              onChange={handleDiscountCurrencyChange}
                              onKeyDown={handleKeyDown}
                              className="discount-input"
                              inputMode="numeric"
                            />
                            <span className="input-suffix">€</span>
                          </div>

                          <span className="discount-divider">{t("or")}</span>

                          <div className={`discount-input-container ${discountPercentage ? "active" : ""}`}>
                            <input
                              type="number"
                              placeholder="0"
                              value={discountPercentage || ""}
                              onChange={handleDiscountPercentageChange}
                              onKeyDown={handleKeyDown}
                              max="100"
                              className="discount-input"
                              inputMode="numeric"
                            />
                            <span className="input-suffix">%</span>
                          </div>
                        </div>
                      </div>

                      <div className="discount-duration">
                        <h4 className="duration-title">{t("duration-of-validity")}</h4>
                        <div className="duration-options-row">
                          {firstThreePeriods.map((period, index) => (
                            <button
                              key={index}
                              className={`duration-option ${selectedDiscountPeriod === index ? "selected" : ""}`}
                              onClick={() => handleExpireTimeChange(period.value, index)}
                            >
                              {period.label}
                            </button>
                          ))}
                        </div>
                        <div className="duration-options-row">
                          {lastThreePeriods.map((period, index) => (
                            <button
                              key={index + 3}
                              className={`duration-option ${selectedDiscountPeriod === index + 3 ? "selected" : ""}`}
                              onClick={() => handleExpireTimeChange(period.value, index + 3)}
                            >
                              {period.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Note section (no tags) */}
              <div className="note-section">
                <MessageScriptTextArea
                  title={"Note"}
                  value={note}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)}
                  placeholder={"Écrivez une note à ne pas oublier"}
                  showTagFooter={false}
                />
              </div>

              <div className="chat-scripts-button-container">
                <div
                  className={`${"chat-scripts-button"} ${isConfirmDisabled || loading ? "disabled" : "enabled"}`}
                  onClick={!isConfirmDisabled && !loading ? handleConfirm : undefined}
                >
                  {loading ? <Loader /> : t("confirm")}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {privateFiles.length > 0 && (
        <div className="chat-scripts-blur-overlay">
          <div className="chat-scripts-blur-overlay__content">
            <AppHeader
              title={`${t("private-media")}`}
              showBackButton={true}
              onBackClick={() => {
                if (currentFileIndex > 0) {
                  setCurrentFileIndex((prev) => prev - 1);
                } else if (privateFiles[currentFileIndex]) {
                  handleRemoveFile(privateFiles[currentFileIndex].fileId);
                }
              }}
            />
            <div className="chat-scripts-blur-overlay__preview">
              {privateFiles[currentFileIndex] &&
                (privateFiles[currentFileIndex].fileType?.includes("audio") ? (
                  <div className="audio-preview-container">
                    <img src={AudioIcon} alt="Audio" className="audio-preview-icon" />
                  </div>
                ) : (
                  <img className="chat-scripts-blur-overlay__media" src={privateFiles[currentFileIndex].fileSrc} alt="media" />
                ))}
            </div>
            <div className="chat-scripts-blur-overlay__controls">
              <div className="chat-scripts-blur-overlay__row">
                <span className="chat-scripts-blur-overlay__label">{t("want-blur-media")}</span>
                {privateFiles[currentFileIndex] && (
                  <Switch
                    checked={!!(files.find((f: any) => f.uid === privateFiles[currentFileIndex].fileId)?.isPrivate ?? true)}
                    onChange={(checked) => handleLockSwitchChange(checked, privateFiles[currentFileIndex])}
                    className="chat-scripts-blur-overlay__switch"
                  />
                )}
              </div>
              <div className="chat-scripts-blur-overlay__desc">{t("want-blur-media-desc")}</div>
              <div className="chat-scripts-blur-overlay__next">
                {privateFiles[currentFileIndex] && (
                  <StickyButton
                    title={t("next") as string}
                    handleClickButton={() => onNextClick(privateFiles[currentFileIndex])}
                    backgroundGreen={true}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {loading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            zIndex: 9999,
          }}
        >
          <SpinnerComponent progress={true} progressValue={progress} message={t("stay-on-page-during-uploading") as string} />
        </div>
      )}
    </div>
  );
};

export default ChatScriptUpdateView;
